/**
 * VerifyPage — /verify
 *
 * Public document-authenticity verification page. No authentication required.
 * Two methods:
 *   1. Upload PDF — SHA-256 is computed client-side via Web Crypto API so the
 *      actual file never leaves the browser; only the hash is sent to the API.
 *   2. Enter Document ID — the session UUID printed on the Certificate of
 *      Completion page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface VerifyResult {
  verified: boolean;
  status?: string;
  message: string;
  signer?: { name: string };
  document?: { name: string; sessionId: string; signedAt: string };
  stats?: { auditEventCount: number };
}

type Tab = 'upload' | 'id';
type State = 'idle' | 'hashing' | 'verifying' | 'done';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function computeSha256(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const hashBuffer = await crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(hashBuffer))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function fmt(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
  });
}

// ---------------------------------------------------------------------------
// Global styles (injected once, same pattern as SigningPage)
// ---------------------------------------------------------------------------

const CSS = `
  *, *::before, *::after { box-sizing: border-box; }
  body { margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f3f4f6; color: #111827; }
  input, button { font-family: inherit; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
`;
function injectStyles() {
  if (document.getElementById('verify-css')) return;
  const el = document.createElement('style');
  el.id = 'verify-css';
  el.textContent = CSS;
  document.head.appendChild(el);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '6px 0', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <span style={{ fontSize: '13px', color: '#6b7280', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: '13px', fontWeight: 600, textAlign: 'right', wordBreak: 'break-all' }}>{value}</span>
    </div>
  );
}

function Spinner({ color = '#2563EB' }: { color?: string }) {
  return (
    <div style={{ width: 20, height: 20, border: `2px solid ${color}33`, borderTopColor: color, borderRadius: '50%', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function VerifyPage() {
  injectStyles();

  const [tab, setTab] = useState<Tab>('upload');
  const [state, setState] = useState<State>('idle');
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Upload tab state
  const [file, setFile] = useState<File | null>(null);
  const [hash, setHash] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ID tab state
  const [sessionId, setSessionId] = useState('');

  const brand = '#2563EB';

  // ── File handling ──────────────────────────────────────────────────────

  const handleFile = useCallback((f: File) => {
    if (f.type !== 'application/pdf' && !f.name.toLowerCase().endsWith('.pdf')) {
      setError('Please upload a PDF file.');
      return;
    }
    setFile(f);
    setHash(null);
    setResult(null);
    setError(null);
  }, []);

  useEffect(() => {
    if (!file) return;
    setState('hashing');
    computeSha256(file)
      .then((h) => { setHash(h); setState('idle'); })
      .catch(() => { setError('Could not read the file.'); setState('idle'); });
  }, [file]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  // ── API calls ──────────────────────────────────────────────────────────

  const verifyHash = async () => {
    if (!hash) return;
    setState('verifying');
    setResult(null);
    setError(null);
    try {
      const res = await fetch('/api/verify/hash', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hash }),
      });
      const data: VerifyResult = await res.json();
      if (res.status === 429) { setError('Too many requests. Please wait a minute.'); setState('idle'); return; }
      setResult(data);
      setState('done');
    } catch {
      setError('Network error. Please try again.');
      setState('idle');
    }
  };

  const verifyId = async () => {
    const id = sessionId.trim();
    if (!id) return;
    setState('verifying');
    setResult(null);
    setError(null);
    try {
      const res = await fetch(`/api/verify/${encodeURIComponent(id)}`);
      if (res.status === 429) { setError('Too many requests. Please wait a minute.'); setState('idle'); return; }
      if (res.status === 400) { setError('Invalid Document ID format. Please enter a valid UUID.'); setState('idle'); return; }
      const data: VerifyResult = await res.json();
      setResult(data);
      setState('done');
    } catch {
      setError('Network error. Please try again.');
      setState('idle');
    }
  };

  // ── Layout ─────────────────────────────────────────────────────────────

  const s = {
    page: { minHeight: '100vh', background: '#f3f4f6', display: 'flex', flexDirection: 'column' as const },
    header: {
      background: brand, padding: '16px 24px',
      display: 'flex', alignItems: 'center', gap: '12px',
    },
    main: { flex: 1, display: 'flex', justifyContent: 'center', padding: '32px 16px' },
    card: {
      background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '560px',
      padding: '32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      height: 'fit-content',
    },
    tabBar: { display: 'flex', gap: '0', marginBottom: '24px', borderRadius: '8px', overflow: 'hidden', border: '1.5px solid #e5e7eb' },
    tab: (active: boolean): React.CSSProperties => ({
      flex: 1, padding: '10px', border: 'none', cursor: 'pointer', fontSize: '14px',
      fontWeight: active ? 700 : 500, transition: 'all 0.15s',
      background: active ? brand : '#f9fafb',
      color: active ? '#fff' : '#6b7280',
    }),
    dropZone: (drag: boolean): React.CSSProperties => ({
      border: `2px dashed ${drag ? brand : '#d1d5db'}`,
      borderRadius: '12px', padding: '36px 20px', textAlign: 'center' as const,
      cursor: 'pointer', background: drag ? '#f0f4ff' : '#fafafa',
      transition: 'all 0.15s',
    }),
    btn: (disabled: boolean): React.CSSProperties => ({
      width: '100%', padding: '13px', marginTop: '16px',
      background: disabled ? '#9ca3af' : brand,
      color: '#fff', border: 'none', borderRadius: '8px',
      fontSize: '15px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
    }),
  };

  const isVerifying = state === 'verifying';
  const isHashing = state === 'hashing';

  return (
    <div style={s.page}>
      {/* Header */}
      <header style={s.header}>
        <img src="/logo-full.png" alt="SignShield" style={{ height: '28px', width: 'auto' }} />
        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '14px', marginLeft: '6px' }}>
          · Document Verification
        </span>
      </header>

      <main style={s.main}>
        <div style={s.card}>
          <h1 style={{ margin: '0 0 6px', fontSize: '22px', fontWeight: 800 }}>Verify a Document</h1>
          <p style={{ margin: '0 0 24px', color: '#6b7280', fontSize: '14px', lineHeight: 1.5 }}>
            Confirm that a document signed via SignShield is authentic and has not been altered since signing.
          </p>

          {/* Tabs */}
          <div style={s.tabBar}>
            <button style={s.tab(tab === 'upload')} onClick={() => { setTab('upload'); setResult(null); setError(null); }}>
              📄 Upload PDF
            </button>
            <button style={s.tab(tab === 'id')} onClick={() => { setTab('id'); setResult(null); setError(null); }}>
              🔑 Enter Document ID
            </button>
          </div>

          {/* Upload tab */}
          {tab === 'upload' && (
            <>
              <div
                style={s.dropZone(dragging)}
                onDrop={handleDrop}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
                  style={{ display: 'none' }}
                />
                {file ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '22px' }}>📄</span>
                    <div style={{ textAlign: 'left' }}>
                      <p style={{ margin: 0, fontWeight: 600, fontSize: '14px' }}>{file.name}</p>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
                        {(file.size / 1024).toFixed(0)} KB
                        {isHashing && ' · Computing hash…'}
                        {hash && !isHashing && ` · SHA-256: ${hash.slice(0, 12)}…`}
                      </p>
                    </div>
                    {isHashing && <Spinner />}
                  </div>
                ) : (
                  <>
                    <p style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 600 }}>Drop PDF here or click to browse</p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                      The SHA-256 hash is computed in your browser — the file is never uploaded.
                    </p>
                  </>
                )}
              </div>

              {hash && !isHashing && (
                <p style={{ margin: '10px 0 0', fontSize: '11px', color: '#9ca3af', wordBreak: 'break-all' }}>
                  SHA-256: {hash}
                </p>
              )}

              <button
                style={s.btn(!hash || isHashing || isVerifying)}
                disabled={!hash || isHashing || isVerifying}
                onClick={verifyHash}
              >
                {isVerifying ? <><Spinner color="#fff" /> Verifying…</> : 'Verify Document'}
              </button>
            </>
          )}

          {/* Document ID tab */}
          {tab === 'id' && (
            <>
              <p style={{ margin: '0 0 10px', fontSize: '13px', color: '#6b7280' }}>
                Enter the Document ID printed on the Certificate of Completion page.
              </p>
              <input
                type="text"
                placeholder="e.g. 5ffed1a7-a8e3-4b2a-99e4-1f09871018da"
                value={sessionId}
                onChange={(e) => { setSessionId(e.target.value); setResult(null); setError(null); }}
                onKeyDown={(e) => e.key === 'Enter' && !isVerifying && sessionId.trim() && verifyId()}
                style={{
                  width: '100%', padding: '11px 14px', border: '1.5px solid #d1d5db',
                  borderRadius: '8px', fontSize: '14px', outline: 'none',
                  fontFamily: 'monospace',
                }}
                onFocus={(e) => (e.target.style.borderColor = brand)}
                onBlur={(e) => (e.target.style.borderColor = '#d1d5db')}
              />
              <button
                style={s.btn(!sessionId.trim() || isVerifying)}
                disabled={!sessionId.trim() || isVerifying}
                onClick={verifyId}
              >
                {isVerifying ? <><Spinner color="#fff" /> Verifying…</> : 'Verify Document'}
              </button>
            </>
          )}

          {/* Error */}
          {error && (
            <div style={{ marginTop: '16px', padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', fontSize: '13px', color: '#991b1b' }}>
              {error}
            </div>
          )}

          {/* Result */}
          {result && (
            <div
              style={{
                marginTop: '20px', padding: '20px', borderRadius: '12px',
                background: result.verified ? '#ecfdf5' : '#fef2f2',
                border: `1px solid ${result.verified ? '#a7f3d0' : '#fecaca'}`,
                animation: 'fadeIn 0.3s ease',
              }}
            >
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0 }}>
                  {result.verified ? '✅' : '❌'}
                </span>
                <div style={{ flex: 1 }}>
                  <p style={{
                    margin: '0 0 14px', fontWeight: 700, fontSize: '14px', lineHeight: 1.4,
                    color: result.verified ? '#065f46' : '#991b1b',
                  }}>
                    {result.message}
                  </p>

                  {result.verified && result.document && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                      <InfoRow label="Document" value={result.document.name} />
                      <InfoRow label="Signer" value={result.signer?.name ?? '—'} />
                      <InfoRow label="Signed" value={fmt(result.document.signedAt)} />
                      <InfoRow label="Audit Events" value={String(result.stats?.auditEventCount ?? 0)} />
                      <InfoRow label="Document ID" value={result.document.sessionId} />
                    </div>
                  )}

                  {!result.verified && result.status && (
                    <div style={{ display: 'inline-block', padding: '4px 10px', background: '#fff', borderRadius: '20px', fontSize: '12px', fontWeight: 600, color: '#92400e' }}>
                      Status: {result.status}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer style={{ textAlign: 'center', padding: '20px 16px' }}>
        <a href="https://merchantkits.com/signshield" target="_blank" rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none', opacity: 0.5 }}>
          <img src="/logo-full.png" alt="SignShield" style={{ height: '20px', width: 'auto' }} />
        </a>
      </footer>
    </div>
  );
}
